import { FilteringList, ListItem, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { IIdentityPool } from '~src/authentication/serverApi/types/identityPools/IIdentityPool';
import { CreateUserIdentityService } from '~src/authentication/services/CreateUserIdentityService';
import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import {
  createItemElement,
  filterPanelStyle,
  filterPanelStyleWithoutBorder,
  INPUT_DEBOUNCE_MS,
  noResults,
  prepareSearchTextInput,
  prompt,SEARCH_TIMEOUT_MS } from '~src/userManagement/components/AddActiveDirectoryGroupDialog';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';

import { SearchActiveDirectoryUserNameResult, User } from '../requests/SearchActiveDirectoryUserNameRequest';

export type CreateActiveDirectoryUserIdentityDialogBodyProps = {
  readonly prompt: string;
  readonly identityPool: IIdentityPool;
  readonly user: IUser;
  readonly isServerUser: boolean;
};

export type CreateActiveDirectoryUserIdentityDialogState = {
  readonly username: string;
};

export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<CreateActiveDirectoryUserIdentityDialogBodyProps, CreateActiveDirectoryUserIdentityDialogState>;

export function CreateActiveDirectoryUserIdentityDialogBody(props: PropsForConfirmActionDialogBody): JSX.Element {
  const [searchText, setSearchText] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasMoreItems, setHasMoreItems] = React.useState(false);
  const [searchTimeoutExceeded , setSearchTimeoutExceeded] = React.useState(false);

  const onSelectUser = React.useCallback(
    (value: string) => {
      const item = items.find((item) => item.value === value);
      if (item) {
        props.updateDialogState({ ...props.dialogState, username: item.value });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, items]
  );

  const onUserFilterChange = React.useCallback(
    (value: string) => {
      setItems([]);
      setIsSearching(true);
      setSearchText(value);
    },
    [setItems, setIsSearching, setSearchText]
  );

  const useSearchTerm = () => {
    setSearchTimeoutExceeded(false);
    const createdItem = {
      content: createItemElement(searchText),
      value: 'searchTextId',
      testId: 'list-item-stop-searching',
      upn: '',
      displayname: searchText
    };

    setItems([createdItem]);
  };

  React.useEffect(() => {
    let ignore = false;
    const fetchUsers = async () => {
      let userResult: SearchActiveDirectoryUserNameResult;

      if (searchText.length > 0 && !ignore) {
        const triggerSearchTimeout = setTimeout(() => {
          setSearchTimeoutExceeded(true);
          setHasMoreItems(false);
        }, SEARCH_TIMEOUT_MS);

        userResult = await CreateUserIdentityService.searchActiveDirectoryUserName(prepareSearchTextInput(searchText));
        let newItems: ListItem[] = [];
        if (userResult !== undefined) {
          setSearchTimeoutExceeded(false);
          clearTimeout(triggerSearchTimeout);
          if (userResult.users && userResult.users.length > 0) {
            newItems = userResult.users.map((user: User) => ({
              content: createItemElement(user.username),
              value: user.username,
              testId: 'list-item-' + user.username,
              upn: user.upn,
              displayname: user.displayName
            }));
          }

          if (!ignore) {
            setItems(newItems);
            setHasMoreItems(userResult.hasMoreItems);
          }

          if (!newItems.find((item) => item.value === props.dialogState.username)){
            props.updateDialogState({ ...props.dialogState, username: undefined });
          }

          if (newItems.length === 0) {
            setIsSearching(false);
          }
        }
      } else {
        setItems([]);
        setSearchTimeoutExceeded(false);
        props.updateDialogState({ ...props.dialogState, username: undefined });
      }
    };

    const debouncedUpdate = _.debounce(fetchUsers, INPUT_DEBOUNCE_MS);
    debouncedUpdate();

    return () => { ignore = true; };
  },

  // eslint-disable-next-line react-hooks/exhaustive-deps
  [searchText]);

  React.useEffect(() => {
    if (items.length > 0) {
      setIsSearching(false);
    }
  }, [items]);

  const isLdapStore = ServerSettingsUnauthenticated.isLdapStore();
  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <div className="tb-add-groups-dialog">
      <div className={prompt}>
        {props.prompt}
      </div>

      <label>{Messages.identityPools_CreateActiveDirectoryIdentityAction_IdentifierLabel({DIRECTORY_NAME: isLdapStore ? 'LDAP': 'Active Directory'})}</label>

      <div style={ items.length > 0 ? { height: 150 } : {height: 'auto'}}>
        <FilteringList
          items={items}
          onSelect={onSelectUser}
          searchPlaceholder={Messages.search()}
          testId="filtering-list-add-AD-user"
          style={searchText.length === 0 ? filterPanelStyleWithoutBorder : filterPanelStyle}
          onFilterChange={onUserFilterChange}
          searchText={searchText}
          autoFocus={true}
          inputTestId="user-picker-filter"
          listTestId="user-picker-list"
        >
          {searchText.length > 0 && items.length === 0 &&
            <div className={noResults} data-tb-test-id="filtering-list-message">
              {isSearching ? Messages.addUsersImportAdGroupSearching() : Messages.noMatchingResults()}
            </div>
          }
        </FilteringList>
      </div>
      {searchTimeoutExceeded &&
        <div className="tb-add-groups-dialog-timeout-message" data-tb-test-id="add-adUser-stop-searching">
          <TextLinkWidget
          testId="add-adUser-stop-searching"
          onClick={useSearchTerm}>
          {Messages.addUsersImportAdGroupSearchingTimeoutExceededLink({SEARCH_TERM: searchText})}
          </TextLinkWidget>
        </div>}
      {items.length > 0 && hasMoreItems && <div className="tb-add-groups-dialog-message" data-tb-test-id="refine-search-message">
        <span>
          {Messages.addUsersImportAdGroupLimit()}
        </span>
      </div>}
    </div>
  );
}
